import React, { useState, useRef, useEffect } from 'react';
import api from '../../config/api';
import QuantityInput from './QuantityInput';
import '../../styles/design-tokens.css';

interface AddToCartControlProps {
  productId: number;
  minimumOrderQuantity?: number;
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
  initialQuantity?: number;
  onQuantityChange?: (quantity: number) => void;
  onCartUpdate?: () => void;
  className?: string;
}

const AddToCartControl: React.FC<AddToCartControlProps> = ({
  productId,
  minimumOrderQuantity = 1,
  disabled = false,
  size = 'sm',
  initialQuantity = 0,
  onQuantityChange,
  onCartUpdate,
  className = ''
}) => {
  const [localQuantity, setLocalQuantity] = useState(initialQuantity);
  const [loading, setLoading] = useState(false);
  const [buttonState, setButtonState] = useState<'default' | 'success' | 'removed' | 'updated'>('default');
  const quantityRef = useRef(initialQuantity); // Track quantity synchronously
  const inputRef = useRef<HTMLInputElement>(null); // Reference to the input element

  // Sync local quantity when initialQuantity prop changes (e.g., when cart loads)
  useEffect(() => {
    setLocalQuantity(initialQuantity);
    quantityRef.current = initialQuantity;
  }, [initialQuantity]);

  const setButtonFeedback = (state: 'success' | 'removed' | 'updated') => {
    setButtonState(state);
    // Reset after 2 seconds
    setTimeout(() => {
      setButtonState('default');
    }, 2000);
  };

  const handleLocalQuantityChange = (newQuantity: number) => {
    setLocalQuantity(newQuantity);
    quantityRef.current = newQuantity; // Update ref synchronously
    // Notify parent of quantity change
    if (onQuantityChange) {
      onQuantityChange(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    try {
      setLoading(true);

      // Use ref to get the most current quantity value (synchronous)
      const currentQuantity = quantityRef.current;

      // Get current cart to check if item exists
      const cartResponse = await api.get('/cart');
      const items = cartResponse.data?.items || cartResponse.data?.cart?.items || [];
      const cartItem = items.find((item: any) => {
        const itemProductId = item.product_id || item.product?.id;
        return itemProductId === productId;
      });

      if (currentQuantity === 0) {
        // Remove from cart if quantity is 0
        if (cartItem) {
          await api.delete(`/cart/items/${cartItem.id}`);
          setButtonFeedback('removed');
        }
        // If no cart item exists and quantity is 0, just ignore (nothing to do)
      } else if (cartItem) {
        // Update existing cart item - set to exact quantity (overwrite)
        await api.put(`/cart/items/${cartItem.id}`, {
          quantity: currentQuantity
        });
        setButtonFeedback('updated');
      } else {
        // Add new item to cart
        await api.post('/cart/items', {
          product_id: productId,
          quantity: currentQuantity
        });
        setButtonFeedback('success');
      }

      // Notify cart was updated
      window.dispatchEvent(new Event('cartUpdated'));

      // Call optional callback
      if (onCartUpdate) {
        onCartUpdate();
      }
    } catch (error: any) {
      console.error('Error updating cart:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`d-flex align-items-center justify-content-center gap-2 ${className}`}>
      <QuantityInput
        value={localQuantity}
        onChange={handleLocalQuantityChange}
        min={0}
        size={size}
        disabled={disabled || loading}
        inputRef={inputRef}
      />
      <button
        className="btn btn-sm"
        onMouseDown={(e) => {
          // Read value directly from input element
          if (inputRef.current) {
            const inputValue = inputRef.current.value;
            const parsedValue = parseInt(inputValue) || 0;

            // Update the ref with the actual input value
            quantityRef.current = parsedValue;

            // Also update state and notify parent
            setLocalQuantity(parsedValue);
            if (onQuantityChange) {
              onQuantityChange(parsedValue);
            }

            // Small delay to let React re-render finish before updating cart
            setTimeout(() => {
              handleAddToCart();
            }, 100);
          }
        }}
        disabled={disabled || loading || buttonState !== 'default'}
        style={{
          backgroundColor: buttonState === 'success' ? '#22c55e' :
                          buttonState === 'removed' ? '#94a3b8' :
                          buttonState === 'updated' ? '#3b82f6' :
                          'var(--color-success, #2E7D32)',
          color: 'white',
          border: 'none',
          transition: 'all 0.3s ease',
          padding: size === 'sm' ? '6px 12px' : size === 'md' ? '8px 16px' : '10px 20px',
          fontSize: size === 'sm' ? '13px' : size === 'md' ? '14px' : '16px',
          minWidth: size === 'sm' ? '60px' : size === 'md' ? '70px' : '80px',
          height: '32px',
          fontWeight: '600'
        }}
      >
        {loading ? (
          <span className="spinner-border spinner-border-sm" role="status"></span>
        ) : buttonState === 'success' ? (
          'Added!'
        ) : buttonState === 'removed' ? (
          'Removed'
        ) : buttonState === 'updated' ? (
          'Updated'
        ) : (
          'Add'
        )}
      </button>
    </div>
  );
};

export default AddToCartControl;
